import type React from "react";
import { useEffect, useRef, useState } from "react";

import { faGear } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled, { useTheme } from "styled-components";

import {
  AudioIcon,
  AudioIconDisabled,
  EndCallIcon,
  MaximiseIcon,
  VideoIcon,
  VideoIconDisabled,
} from "assets/videocall";
import { useWindowSize } from "utils/hooks";

import { useAudioDevices } from "../hooks/useAudioDevices";
import type { VideoCallMode } from "../types";

import { AudioDeviceSelector } from "./AudioDeviceSelector";
import { ViewModeSelector } from "./ViewModeSelector";

interface Props {
  readonly audioEnabled: boolean;
  readonly minimised: boolean;
  readonly videoCallMode: VideoCallMode;
  readonly videoEnabled: boolean;
  readonly onAudioEnabledToggled: () => void;
  readonly onEndPressed: () => void;
  readonly onModeChange: (mode: VideoCallMode) => void;
  readonly onVideoEnabledToggled: () => void;
  readonly onAudioInputChange?: (deviceId: string) => void;
  readonly onAudioOutputChange?: (deviceId: string) => void;
}

export const CallButtons: React.FC<Props> = ({
  audioEnabled,
  minimised,
  videoCallMode,
  videoEnabled,
  onAudioEnabledToggled,
  onEndPressed,
  onModeChange,
  onVideoEnabledToggled,
  onAudioInputChange,
  onAudioOutputChange,
}) => {
  const { width } = useWindowSize();
  const theme = useTheme();
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close popovers on click outside
  useEffect(() => {
    if (!showDeviceSelector && !showModeSelector) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDeviceSelector(false);
        setShowModeSelector(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [showDeviceSelector, showModeSelector]);

  const { audioInputs, audioOutputs, selectedInputId, selectedOutputId, setSelectedInputId, setSelectedOutputId } =
    useAudioDevices();

  const hasAudioDevices = audioInputs.length > 0 || audioOutputs.length > 0;
  const isDesktop = width > parseInt(theme.breakpoint, 10);

  const handleInputChange = (deviceId: string) => {
    setSelectedInputId(deviceId);
    onAudioInputChange?.(deviceId);
  };

  const handleOutputChange = (deviceId: string) => {
    setSelectedOutputId(deviceId);
    onAudioOutputChange?.(deviceId);
  };

  const handleModeChange = (mode: VideoCallMode) => {
    onModeChange(mode);
    setShowModeSelector(false);
  };

  return (
    <Container ref={containerRef} $minimised={minimised}>
      {!minimised ? (
        <>
          <ControlButton data-testid="video-call-hangup-button" onClick={onEndPressed}>
            <IconEndCall />
          </ControlButton>
          <Filler />
          <ControlButton onClick={onVideoEnabledToggled}>
            {videoEnabled ? <EnabledVideo /> : <DisabledVideo />}
          </ControlButton>
          <ControlButton onClick={onAudioEnabledToggled}>
            {audioEnabled ? <EnabledAudio /> : <DisabledAudio />}
          </ControlButton>
          <Filler />
          {isDesktop && (
            <ControlButton
              onClick={() => {
                setShowModeSelector(!showModeSelector);
                setShowDeviceSelector(false);
              }}
            >
              <ViewModeIcon />
            </ControlButton>
          )}
          {hasAudioDevices && (
            <ControlButton
              onClick={() => {
                setShowDeviceSelector(!showDeviceSelector);
                setShowModeSelector(false);
              }}
              title="Audio Settings"
            >
              <CircleIconWrapper>
                <SettingsIcon icon={faGear} />
              </CircleIconWrapper>
            </ControlButton>
          )}
        </>
      ) : (
        <>
          <Filler />
          <ControlButton onClick={() => setShowModeSelector(!showModeSelector)}>
            <ViewModeIcon />
          </ControlButton>
        </>
      )}

      {showModeSelector && (
        <PopoverAnchor>
          <ViewModeSelector
            currentMode={videoCallMode}
            onModeChange={handleModeChange}
            onClose={() => setShowModeSelector(false)}
          />
        </PopoverAnchor>
      )}
      {showDeviceSelector && hasAudioDevices && (
        <PopoverAnchor>
          <AudioDeviceSelector
            audioInputs={audioInputs}
            audioOutputs={audioOutputs}
            selectedInputId={selectedInputId}
            selectedOutputId={selectedOutputId}
            onInputChange={handleInputChange}
            onOutputChange={handleOutputChange}
            onClose={() => setShowDeviceSelector(false)}
          />
        </PopoverAnchor>
      )}
    </Container>
  );
};

const Container = styled.div<{ $minimised: boolean }>`
  bottom: ${props => (props.$minimised ? "10px" : props.theme.spacing.S_20)};
  display: flex;
  align-items: center;
  left: 0;
  position: absolute;
  padding: ${props => (props.$minimised ? "0 10px" : "0 32px")};
  right: 0;
`;

const PopoverAnchor = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 0;
`;

const ControlButton = styled.button`
  border: 1px solid transparent;
  background: none;
  margin: 0 6px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
  }
  &:focus {
    outline: none;
  }
`;

const IconEndCall = styled(EndCallIcon)`
  width: 53px;
  height: 53px;
`;

const EnabledVideo = styled(VideoIcon)`
  width: 53px;
  height: 53px;
`;

const DisabledVideo = styled(VideoIconDisabled)`
  width: 53px;
  height: 53px;
`;

const EnabledAudio = styled(AudioIcon)`
  width: 53px;
  height: 53px;
`;

const DisabledAudio = styled(AudioIconDisabled)`
  width: 53px;
  height: 53px;
`;

const Filler = styled.div`
  flex: 1;
`;

const CircleIconWrapper = styled.div`
  width: 53px;
  height: 53px;
  border-radius: 50%;
  background: rgba(56, 74, 95, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SettingsIcon = styled(FontAwesomeIcon)`
  width: 20px;
  height: 20px;
  color: white;
`;

const ViewModeIcon = styled(MaximiseIcon)`
  width: 53px;
  height: 53px;
`;
